import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Save, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { COLLECTIONS } from "./schemas";
import { Field } from "./Fields";
import { PageTitle } from "./AdminDashboard";
import { useContent } from "../context/ContentContext";

const base = process.env.REACT_APP_BACKEND_URL;
const resolve = (u) => (u && u.startsWith("/api/") ? base + u : u);

const Editor = ({ schema, item, onClose, onSaved, col }) => {
  const isNew = !item.id;
  const [form, setForm] = useState(item);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = isNew ? await api.post(`/admin/${col}`, form) : await api.put(`/admin/${col}/${item.id}`, form);
      toast.success("Saved");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-jannah-green-dark/60 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl h-full bg-white shadow-2xl flex flex-col" data-testid="admin-editor">
        <div className="flex items-center justify-between px-8 py-5 border-b border-jannah-sand">
          <h2 className="text-xl font-bold text-jannah-ink">{isNew ? `New ${schema.singular}` : `Edit ${schema.singular}`}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded hover:bg-jannah-cream" data-testid="admin-editor-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-8 py-6 grid sm:grid-cols-2 gap-5 content-start">
          {schema.fields.map((f) => (
            <Field key={f.key} field={f} value={form[f.key]} disabled={f.key === "id" && !isNew} onChange={(v) => setForm({ ...form, [f.key]: v })} />
          ))}
        </div>
        <div className="px-8 py-5 border-t border-jannah-sand flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-jannah-sand text-sm font-semibold">Cancel</button>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-jannah-gold text-white text-sm font-bold uppercase tracking-wider hover:bg-jannah-green disabled:opacity-60" data-testid="admin-editor-save">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminCollection = () => {
  const { col } = useParams();
  const schema = COLLECTIONS[col];
  const { reload } = useContent();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get(`/admin/${col}`).then((r) => setItems(r.data)), [col]);
  useEffect(() => { load(); setEditing(null); }, [load]);

  const remove = async (it) => {
    if (!window.confirm(`Delete "${schema.listTitle(it)}"?`)) return;
    await api.delete(`/admin/${col}/${it.id}`);
    toast.success("Deleted");
    load(); reload();
  };
  const move = async (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.put(`/admin-reorder/${col}`, { ids: next.map((x) => x.id) });
    reload();
  };

  if (!schema) return <div>Unknown collection</div>;
  return (
    <div data-testid={`admin-collection-${col}`}>
      <PageTitle title={schema.title} sub={`${items.length} items · use the arrows to change display order`}>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-jannah-gold text-white text-sm font-bold uppercase tracking-wider hover:bg-jannah-green" data-testid="admin-add-btn"><Plus size={16} /> Add {schema.singular}</button>
      </PageTitle>
      <div className="rounded-2xl bg-white border border-jannah-sand divide-y divide-jannah-sand">
        {items.length === 0 && <div className="p-8 text-sm text-jannah-ink/50 text-center">Nothing here yet. Click “Add” to create the first one.</div>}
        {items.map((it, i) => (
          <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`admin-row-${it.id}`}>
            {schema.listImage && (
              <div className="w-16 h-12 rounded-md bg-jannah-cream overflow-hidden shrink-0">{schema.listImage(it) && <img src={resolve(schema.listImage(it))} alt="" className="w-full h-full object-cover" />}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-jannah-ink truncate">{schema.listTitle(it)}</div>
              <div className="text-xs text-jannah-ink/55 truncate">{schema.listSub(it)}</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} className="p-2 rounded hover:bg-jannah-cream text-jannah-ink/60" title="Move up"><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} className="p-2 rounded hover:bg-jannah-cream text-jannah-ink/60" title="Move down"><ArrowDown size={15} /></button>
              <button onClick={() => setEditing(it)} className="p-2 rounded hover:bg-jannah-green/10 text-jannah-green" title="Edit" data-testid={`admin-edit-${it.id}`}><Pencil size={15} /></button>
              <button onClick={() => remove(it)} className="p-2 rounded hover:bg-red-50 text-red-500" title="Delete" data-testid={`admin-delete-${it.id}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <Editor schema={schema} item={editing} col={col} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); reload(); }} />}
    </div>
  );
};

export default AdminCollection;
